import React, { useEffect, useMemo, useState, useCallback } from 'react';
import {
    Container,
    Typography,
    CircularProgress,
    Backdrop,
    useTheme,
    FormControlLabel,
    Checkbox,
    Paper,
    Link,
    Skeleton,
    Snackbar,
    Alert,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import ConsentToggle from './ConsentToggle';
import { updateConsent, getConsentStatus, UpdateConsentPayload } from '../api';
import { useNavigate } from 'react-router-dom';
import { getNewStoreData } from 'helper/util';
import WebviewModal from './WebviewModal';

interface ConsentType {
    marketing_email: boolean;
    marketing_sms: boolean;
    email: string;
    phone: string;
}

const ConsentManager = () => {
    const theme = useTheme();
    const navigate = useNavigate();

    const [consentState, setConsentState] = useState<ConsentType>({
        email: '',
        phone: '',
        marketing_email: false,
        marketing_sms: false,
    });

    const [originalConsent, setOriginalConsent] = useState<ConsentType>({
        email: '',
        phone: '',
        marketing_email: false,
        marketing_sms: false,
    });

    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [showEmail, setShowEmail] = useState(false);
    const [showSMS, setShowSMS] = useState(false);
    const [smsPending, setSmsPending] = useState(false);
    const [webviewOpen, setWebviewOpen] = useState(false);
    const [webviewUrl, setWebviewUrl] = useState('');
    const [smsTermsAccepted, setSmsTermsAccepted] = useState(false);
    const [toast, setToast] = useState<{ open: boolean; msg: string; severity: 'success' | 'error' | 'info' }>(
        { open: false, msg: '', severity: 'success' }
    );

    const { profileId, storeId, externalId } = getNewStoreData();

    const handleOpenWebview = (url: string) => {
        setWebviewUrl(url);
        setWebviewOpen(true);
    };
    const handleCloseWebview = () => setWebviewOpen(false);

    const getConsentData = useCallback(async () => {
        setLoading(true);
        try {
            const res = await getConsentStatus(profileId || '', externalId || '');
            const emailConsent = res.consent.email?.status ?? null;
            const smsConsent = res.consent.sms?.status ?? null;

            setConsentState({
                email: res.email ?? '',
                phone: res.phone ?? '',
                marketing_email: emailConsent === 'OPT_IN',
                marketing_sms: smsConsent === 'OPT_IN',
            });

            setOriginalConsent({
                email: res.email ?? '',
                phone: res.phone ?? '',
                marketing_email: emailConsent === 'OPT_IN',
                marketing_sms: smsConsent === 'OPT_IN',
            });

            setShowEmail(!!res.email);
            setShowSMS(!!res.phone);
            setSmsPending(smsConsent === 'PENDING');
            setSmsTermsAccepted(false);
        } catch (e) {
            console.error('Error fetching consent data:', e);
            setToast({ open: true, msg: 'Failed to load consent data', severity: 'error' });
        } finally {
            setLoading(false);
        }
    }, [externalId, profileId]);

    useEffect(() => {
        if (!storeId || !profileId) {
            navigate('/restricted');
        } else {
            // fire and forget
            getConsentData();
        }
    }, [storeId, profileId, getConsentData, navigate]);

    const isDirty = useMemo(
        () =>
            consentState.marketing_email !== originalConsent.marketing_email ||
            consentState.marketing_sms !== originalConsent.marketing_sms,
        [consentState, originalConsent]
    );

    const smsChanged = useMemo(
        () => consentState.marketing_sms !== originalConsent.marketing_sms,
        [consentState.marketing_sms, originalConsent.marketing_sms]
    );

    // Terms are required only if that channel is being toggled to OPT_IN
    const requireSmsTerms = showSMS && smsChanged && consentState.marketing_sms === true;


    const handleSubmit = async () => {
        const payload: UpdateConsentPayload = {
            customerId: profileId || '',
            email: consentState.email,
            phone: consentState.phone || undefined,
            consents: [],
        };

        if (consentState.marketing_email !== originalConsent.marketing_email) {
            payload.consents.push({ channel: 'EMAIL', action: consentState.marketing_email ? 'OPT_IN' : 'OPT_OUT' });
        }
        if (consentState.marketing_sms !== originalConsent.marketing_sms) {
            payload.consents.push({ channel: 'SMS', action: consentState.marketing_sms ? 'OPT_IN' : 'OPT_OUT' });
        }

        if (payload.consents.length === 0) return;

        setSubmitting(true);
        try {
            const response = await updateConsent(payload, externalId || '');

            // reflect server success per channel
            if (response.status.sms === 'success') {
                const smsAction = payload.consents.find((c) => c.channel === 'SMS')?.action;
                setConsentState((prev) => ({ ...prev, marketing_sms: smsAction === 'OPT_IN' }));
                setSmsPending(smsAction === 'OPT_IN');
            }
            if (response.status.email === 'success') {
                const emailAction = payload.consents.find((c) => c.channel === 'EMAIL')?.action;
                setConsentState((prev) => ({ ...prev, marketing_email: emailAction === 'OPT_IN' }));
            }

            setOriginalConsent((prev) => ({
                email: consentState.email,
                phone: consentState.phone,
                marketing_email: response.status.email === 'success' ? consentState.marketing_email : prev.marketing_email,
                marketing_sms: response.status.sms === 'success' ? consentState.marketing_sms : prev.marketing_sms,
            }));

            const updatedChannels: string[] = [];
            const failedChannels: string[] = [];

            if (response.status.email === 'success') updatedChannels.push('Email');
            if (response.status.email === 'fail') failedChannels.push('Email');
            if (response.status.sms === 'success') updatedChannels.push('SMS');
            if (response.status.sms === 'fail') failedChannels.push('SMS');

            if (updatedChannels.length > 0) {
                setToast({
                    open: true,
                    msg: `${updatedChannels.join(' & ')} consent updated successfully.${failedChannels.length ? ` However, ${failedChannels.join(' & ')} update failed.` : ''}`,
                    severity: failedChannels.length ? 'info' : 'success',
                });
            } else {
                setToast({ open: true, msg: `Consent update failed${failedChannels.length ? ` for ${failedChannels.join(' & ')}` : ''}.`, severity: 'error' });
            }

            // Refresh server truth once (no duplicates)
            getConsentData();
        } catch (error) {
            console.error('Error updating consent:', error);
            setToast({ open: true, msg: 'Error updating consent', severity: 'error' });
        } finally {
            setSubmitting(false);
        }
    };

    const SmsTermsBlock: React.FC = () => (
        <Paper
            variant="outlined"
            sx={{
                p: 2,
                mb: 2.5,
                borderRadius: 3,
            }}
        >
            <FormControlLabel
                control={
                    <Checkbox
                        checked={smsTermsAccepted}
                        onChange={(e) => setSmsTermsAccepted(e.target.checked)}
                        name="smsTermsCheckbox"
                        color="primary"
                    />
                }
                label={
                    <>
                        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 1 }}>
                            For Guest Use Only
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            By checking this box, you agree to receive recurring automated marketing text messages (e.g., AI content, cart reminders) from James Avery at the number you provide. Consent not a condition of purchase. Reply HELP for help & STOP to cancel. Msg frequency varies. Msg & data rates may apply. We may share info with service providers per our Privacy Notice.{' '}
                            <Link component="button" onClick={() => handleOpenWebview('https://attnl.tv/t/uO2/r/US')}>Terms</Link> (incl. arbitration) &{' '}
                            <Link component="button" onClick={() => handleOpenWebview('https://attnl.tv/p/uO2')}>Privacy Notice</Link>.
                        </Typography>
                    </>
                }
            />
        </Paper>
    );

    return (
        <>
            {/* Subtle top header */}


            <Container maxWidth="md" sx={{ pt: 2, pb: 12 }}>
                {loading ? (
                    <>
                        <Skeleton variant="rounded" height={76} sx={{ mb: 2 }} />
                        <Skeleton variant="rounded" height={140} sx={{ mb: 2 }} />
                        <Skeleton variant="rounded" height={76} sx={{ mb: 2 }} />
                    </>
                ) : (
                    <>
                        {showEmail && (
                            <ConsentToggle
                                label="Email"
                                checked={consentState.marketing_email}
                                onChange={() => setConsentState((p) => ({ ...p, marketing_email: !p.marketing_email }))}
                            />
                        )}

                        {showSMS && (
                            <ConsentToggle
                                label="SMS"
                                checked={consentState.marketing_sms}
                                onChange={() => setConsentState((p) => ({ ...p, marketing_sms: !p.marketing_sms }))}
                                pending={smsPending}
                            />
                        )}

                        {showSMS && <SmsTermsBlock />}
                    </>
                )}
            </Container>

            {/* Sticky Footer */}
            <Paper
                elevation={8}
                sx={{
                    position: 'fixed',
                    bottom: 0,
                    left: 0,
                    right: 0,
                    py: 1.5,
                    px: 2,
                    borderTopLeftRadius: 16,
                    borderTopRightRadius: 16,
                    backdropFilter: 'blur(8px)',
                }}
            >
                <Container maxWidth="md">
                    <LoadingButton
                        fullWidth
                        variant="contained"
                        loading={submitting}
                        disabled={
                            !isDirty ||
                            (requireSmsTerms && !smsTermsAccepted)
                        }
                        onClick={handleSubmit}
                        sx={{
                            py: 1.5,
                            borderRadius: 3,
                            fontWeight: 700,
                            '&.Mui-disabled': {
                                color: theme.palette.text.primary, // Keep text visible
                                backgroundColor: theme.palette.action.disabledBackground, // Light gray bg
                                opacity: 0.6, // Slightly dimmed, but readable
                            },
                        }}
                    >
                        {submitting ? 'Submitting…' : 'Submit'}
                    </LoadingButton>
                </Container>
            </Paper>

            <WebviewModal open={webviewOpen} url={webviewUrl} onClose={handleCloseWebview} />

            <Backdrop sx={{ color: '#fff', zIndex: (t) => t.zIndex.drawer + 1 }} open={false}>
                <CircularProgress color="inherit" />
            </Backdrop>

            <Snackbar
                open={toast.open}
                autoHideDuration={4000}
                onClose={() => setToast((t) => ({ ...t, open: false }))}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setToast((t) => ({ ...t, open: false }))}
                    severity={toast.severity}
                    sx={{ width: '100%' }}
                >
                    {toast.msg}
                </Alert>
            </Snackbar>
        </>
    );
};

export default ConsentManager;
